import DimensionIcon from "@/components/dimension-icon";
import { Badge } from "@/components/ui/badge";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Card, CardContent } from "@/components/ui/card";
import { ProgressCircle } from "@/components/ui/charts/progress-circle";
import { Progress } from "@/components/ui/progress";
import { useApplicationByEdition } from "@/hooks/application-by-edition.hook";
import { useCurrentUserProfile } from "@/hooks/current-user-profile.hook";
import { useEditionById } from "@/hooks/edition-by-id.hook";
import { useQuizById } from "@/hooks/quiz-by-id.hook";
import { getDimensionById } from "@/lib/dimensions";
import { cn } from "@/lib/utils";
import { mapQuestionnaireResultStatusToLabel } from "@/mappers/results";
import { currentEditionIdAtom } from "@/store/edition-id.store";
import { StatusColor } from "@/types/quizzes";
import { ArrowRight2, Home2 } from "iconsax-react";
import { useSetAtom } from "jotai";
import { useEffect } from "react";
import {
  Link,
  NavLink,
  Outlet,
  useNavigate,
  useParams,
} from "react-router-dom";
import { CompanyDetails } from "./company-details";

const PreviousEditionPage = () => {
  const { editionId = "", dimensionId } = useParams();

  const setEditionIdAtom = useSetAtom(currentEditionIdAtom);

  const navigate = useNavigate();

  const { data: userProfile } = useCurrentUserProfile();

  const {
    data: edition,
    isLoading: isEditionLoading,
    error: editionError,
  } = useEditionById({
    companyId: userProfile?.companyId || "",
    editionId: editionId,
  });

  const { data: application } = useApplicationByEdition({
    companyId: userProfile?.companyId || "",
    editionId: editionId,
  });

  const { data: quiz } = useQuizById({
    companyId: userProfile?.companyId || "",
    applicationQuizId: application?.quizId || "",
  });

  const dimensions = quiz?.dimensionsSummary || [];

  /**
   * Redirect to the first dimension if no dimension is selected
   */
  useEffect(() => {
    if (!dimensionId && dimensions.length > 0) {
      navigate(`${dimensions[0].dimension}`, { replace: true });
    }
  }, [dimensions, dimensionId]);

  /**
   * Set the current edition id atom
   */
  useEffect(() => {
    setEditionIdAtom(editionId);
  }, [editionId]);

  if (isEditionLoading) {
    return <div>Cargando...</div>;
  }

  if (editionError) {
    throw editionError;
  }

  if (!edition) {
    throw new Error("No se encontró la edición");
  }

  return (
    <>
      <Breadcrumb className="mb-6">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to={"/"}>
                <Home2 size={14} className="-mt-0.5 mr-1 inline" /> Inicio
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/previous-editions">Ediciones anteriores</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{edition.name}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {application?.company && quiz && (
        <CompanyDetails
          className="lg:col-span-3"
          companyName={application.company.name}
          companySize={application.company.segment.size}
          sector={application.company.segment.sector}
          subsector={application.company.segment.subsector}
        >
          <div className="flex items-center gap-4">
            <ProgressCircle
              value={quiz.result.score}
              radius={40}
              backgroundVariant={"neutral"}
              foregroundColor={StatusColor[quiz.result.resultStatus]}
              strokeWidth={10}
            >
              <span className="font-medium">{quiz.result.score}</span>
            </ProgressCircle>

            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium">Resultado</span>
              <Badge
                style={{
                  backgroundColor: StatusColor[quiz.result.resultStatus],
                }}
              >
                {mapQuestionnaireResultStatusToLabel(quiz.result.resultStatus)}
              </Badge>
            </div>
          </div>
        </CompanyDetails>
      )}

      <div className="mt-4 flex gap-4">
        <div className="w-full max-w-sm shrink-0">
          <Card className="sticky top-4">
            <CardContent className="pt-6">
              <h2 className="text-lg font-bold">Indicadores</h2>

              <h3 className="my-4 text-xs font-bold text-[#293038]">
                Por dimensión
              </h3>

              <div className="flex flex-col gap-px">
                {dimensions?.map((dimension) => (
                  <NavLink
                    replace
                    key={dimension.dimension}
                    className={({ isActive }) =>
                      cn(
                        "-mx-4 flex cursor-pointer items-center gap-3 rounded px-4 py-2",
                        !isActive && "hover:bg-slate-50",
                        isActive && "bg-[#E3E3F3]",
                      )
                    }
                    to={dimension.dimension}
                  >
                    <DimensionIcon dimensionId={dimension.dimension} />

                    <div className="grow">
                      <div className="mb-px flex text-xs font-medium text-[#4F5D6D]">
                        <div className="grow">{dimension.name}</div>
                        {dimension.group === "IMPACT_DIMENSIONS" && (
                          <div>
                            {dimension.result?.score.toFixed(2)}
                            <span
                              style={{
                                color: getDimensionById(dimension.dimension)
                                  ?.color,
                              }}
                            >
                              /{dimension.result?.scoreProportion}
                            </span>
                          </div>
                        )}
                      </div>

                      <Progress
                        value={
                          dimension.group === "IMPACT_DIMENSIONS"
                            ? dimension.result?.score
                            : 100
                        }
                        className="h-2"
                        indicatorColor={
                          getDimensionById(dimension.dimension)?.color
                        }
                      />
                    </div>

                    <ArrowRight2 size={14} className="-mr-1" />
                  </NavLink>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grow">
          <Outlet />
        </div>
      </div>
    </>
  );
};

export default PreviousEditionPage;
